import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { invoiceListDemoData } from '../../../../../data/invoiceListDemoData';
import { useTemplate } from '../../../../features/template/TemplateProvider';
import useApi from '../../../../hook/useApi';

/**
 * @typedef {Object} Pagination
 * @property {number} [page] - Current page number.
 * @property {number} [pageSize] - Number of items per page.
 * @property {number} [total] - Total number of records.
 */

/**
 * @typedef {Object} QuotationData
 * @property {Array} list - List of quotations/invoices.
 * @property {Pagination} pagination - Pagination information.
 */

/**
 * @typedef {Object} BillingInvoiceListContextValue
 * @property {Function} setParams - Set query/filter parameters.
 * @property {number} reloadKey - Key used to trigger reload.
 * @property {Function} setReloadKey - Update reload key manually.
 * @property {QuotationData} quotationData - Current quotation/invoice data.
 * @property {Function} setQuotationData - Manually update quotation data.
 * @property {Function} handleReload - Force reload of the invoice list.
 */

const BillingQuotationListContext = createContext();
/**
 * Custom hook to access Billing Invoice context.
 * @returns {BillingInvoiceListContextValue} Context value with data and handlers.
 */
export const useBillingQuotation = () => useContext(BillingQuotationListContext);

/**
 * Provider for quotation/invoice list state and data fetching.
 *
 * Handles:
 * - API fetching of invoices.
 * - Reload triggers.
 * - Context sharing for invoice list data and filters.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components using this provider.
 * @returns {JSX.Element} The context provider wrapping its children.
 */
function QuotationListProvider({ children }) {
    const { get } = useApi();
    const [reloadKey, setReloadKey] = useState(0);
    const [quotationData, setQuotationData] = useState({
        quotations: invoiceListDemoData,
        pagination: {}
    });
    const [params, setParams] = useState({});

    const { setMessage } = useTemplate();
    // -- Load invoice list data
    useEffect(() => {
        setQuotationData({ quotations: invoiceListDemoData, pagination: {} });
        if (Object.keys(params).length > 0) {
            get('api/invoices/quotations', params)
                .then(({ error, data, message }) => {
                    if (error) throw new Error(message);

                    setQuotationData(data);
                })
                .catch((err) => {
                    setQuotationData({ quotations: [], pagination: {} });
                    setMessage({ type: 'error', text: err?.message || 'Failed to load data' });
                });
        }
    }, [reloadKey, params]);

    const value = useMemo(() => {
        // --Handle Reload
        const handleReload = () => {
            setReloadKey((d) => d + 1);
        };

        return {
            // Data filter data
            setParams,

            // Reload Key
            reloadKey,
            setReloadKey,
            // -- customer list
            quotationData,
            setQuotationData,

            // All Handler
            handleReload
        };
    }, [quotationData, reloadKey]);

    return (
        <BillingQuotationListContext.Provider value={value}>
            {children}
        </BillingQuotationListContext.Provider>
    );
}

export default QuotationListProvider;
